import React, { useState, useEffect, useRef, useLayoutEffect, useMemo } from 'react'
import './Projects.css'
import { fetchPostsPreview } from '../../services/posts'
import { fetchTags } from '../../services/tags'
import { PROJECT_CATEGORY } from '../../constants/wordpress'
import Navbar from '../../components/navbar/Navbar'
import ListItem from '../../components/listItem/ListItem'
import Footer from '../../components/footer/Footer'
import FilterItem from '../../components/filterItem/FilterItem'
import { randomIntFromInterval } from '../../utils/utils'
import LoadingFull from '../../components/loading/LoadingFull'
import { useNavigate } from 'react-router'
import { debounce } from 'lodash'
import ImageLoader from '../../components/image-loader/ImageLoader'

const { innerWidth: pageWidth, innerHeight: pageHeight } = window
const isMobile = pageWidth <= 650

const IMAGE_WIDTH = 350
const IMAGE_HEIGHT = 250

const getRandomWidth = () => randomIntFromInterval(0, pageWidth - IMAGE_WIDTH)
const getRandomHeight = () => randomIntFromInterval(0, pageHeight - IMAGE_HEIGHT)

const getTagNames = (tags = [], tagsList = []) =>
  tags.map((tag) => {
    const { name = '' } =
      tagsList.length > 0 &&
      tagsList.find(({ id }) => {
        return id === tag
      })
    return name || ''
  })

const extractYear = (content) => {
  if (!content) return null
  const div = document.createElement('div')
  div.innerHTML = content
  const infos = div.getElementsByClassName('infos')
  if (!infos || !infos[0]) return null
  const match = infos[0].textContent.match(/\b(20\d{2})\b/)
  return match ? match[1] : null
}

const Projects = () => {
  const [year, setYear] = useState('')
  const [projectList, setProjectList] = useState([])
  const [tagsList, setTagsList] = useState([])
  const [imageInBackground, setImageInBackground] = useState('')

  const [loading, setLoading] = useState(true)
  const [innerHeight, setInnerHeight] = useState(window.innerHeight)
  const [hasScroll, setHasScroll] = useState(false)

  const navigate = useNavigate()

  useEffect(() => {
    fetchPostsPreview(PROJECT_CATEGORY, 1, 60, setProjectList)
    fetchTags(setTagsList)
  }, [])

  useEffect(() => {
    if (projectList.length > 0 && tagsList.length > 0) setLoading(false)
  }, [projectList.length, tagsList.length])

  const ref = useRef(null)

  useEffect(() => {
    if (!ref || !ref.current) return
    setHasScroll(ref.current.clientHeight + 144 > innerHeight)
  }, [ref.current, innerHeight])

  const debouncedHandleResize = debounce(() => {
    setInnerHeight(window.innerHeight)
  }, 100)

  useLayoutEffect(() => {
    window.addEventListener('resize', debouncedHandleResize)

    return () => {
      window.removeEventListener('resize', debouncedHandleResize)
    }
  })

  const projectsWithYear = useMemo(
    () => projectList.map((p) => ({ ...p, _year: extractYear(p.content?.rendered) })),
    [projectList]
  )

  const availableYears = useMemo(() => {
    const set = new Set(projectsWithYear.map((p) => p._year).filter(Boolean))
    return Array.from(set).sort((a, b) => b - a)
  }, [projectsWithYear])

  const filteredProjects = useMemo(
    () => (year === '' ? projectsWithYear : projectsWithYear.filter((p) => p._year === year)),
    [projectsWithYear, year]
  )

  const imagesPreload =
    !isMobile &&
    filteredProjects?.length > 0 &&
    filteredProjects.slice(0, 5).map(({ jetpack_featured_media_url }) => jetpack_featured_media_url)

  return (
    <div className="page">
      {!isMobile && (
        <div className="project-hidden-section">
          <div
            style={{
              width: IMAGE_WIDTH,
              height: IMAGE_HEIGHT,
              marginLeft: getRandomWidth(),
              marginTop: getRandomHeight(),
              backgroundImage: `url(${imageInBackground})`,
              backgroundPosition: 'center',
              backgroundRepeat: 'no-repeat',
              backgroundSize: 'contain'
            }}
          />
        </div>
      )}
      <div className="page-scrollable-content">
        <Navbar />
        {loading && <LoadingFull />}
        {!loading && (
          <div className="filters-navbar-wrapper">
            <FilterItem label="All" onClick={() => setYear('')} isSelected={year === ''} />
            {availableYears.map((y) => (
              <FilterItem
                key={`filter-${y}`}
                label={y}
                onClick={() => setYear(y)}
                isSelected={year === y}
              />
            ))}
          </div>
        )}
        {!isMobile && imagesPreload?.length > 0 && <ImageLoader imagesToLoad={imagesPreload} />}
        {filteredProjects?.length > 0 && (
          <div className="project-list-wrapper" ref={ref}>
            {filteredProjects.map((project) => (
              <ListItem
                key={project.id}
                item={project}
                styleItem={{ gridTemplateColumns: '0.35fr 0.65fr' }}
                renderFields={({ tags }) => {
                  const tagNames = getTagNames(tags, tagsList)
                  return (
                    <div className="project-list-item">
                      <div></div>
                      {tagNames.sort().map((tag, index) => {
                        const parsedTag = tag[0] === '|' ? tag.replace('|', '') : tag
                        return <div key={`tag-${index}`}>{parsedTag}</div>
                      })}
                    </div>
                  )
                }}
                onMouseEnter={() => setImageInBackground(project.jetpack_featured_media_url)}
                onMouseLeave={() => setImageInBackground('')}
                onClick={() => navigate(`/projects/${project.id}`)}
              />
            ))}
          </div>
        )}
        {hasScroll && <Footer containerStyle={{ marginTop: 0 }} />}
      </div>
      {!hasScroll && <Footer />}
    </div>
  )
}

export default Projects
